import React, { useState, useCallback, useEffect, FC, ReactNode } from 'react';

import PropTypes from 'prop-types';

import {
  fetchUser,
  getCurrentTokenWithoutRedirect,
} from '@adsk/acs-sc-web-platform-api';
import { useSWRAlloy, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

type CurrentUser = ReturnType<typeof fetchUser> extends Promise<infer T>
  ? T
  : never;

type UserContextValue = {
  data: null | CurrentUser;
  error: null | unknown;
  loading: boolean;
  retry(): void;
  setUser(data: UserContextValue['data']): void;
  setEnabled?: (enabled: boolean) => void;
};

type UserProviderProps = {
  redirectLoggedOutUsers?: boolean;
  disabled?: boolean;
  children?: ReactNode;
};

/**
 * Context for current user.
 */
const UserContext = getOrCreateGlobalContext<UserContextValue>(
  CONTEXT_NAMES.UserContext,
);

const useUserContext = () =>
  useContextWithValidation(UserContext, {
    hook: 'useUser',
    provider: 'UserProvider',
  });

export const useOptionalUser = () => {
  const { setEnabled, ...rest } = useUserContext();
  useEffect(() => {
    setEnabled && setEnabled(true);
  }, [setEnabled]);
  return rest;
};

export const UserProvider: FC<UserProviderProps> = ({
  children,
  redirectLoggedOutUsers,
  disabled,
}) => {
  const [enabled, setEnabled] = useState(!disabled);
  const {
    data: fetchData,
    error,
    revalidate: retry,
    mutate,
  } = useSWRAlloy(enabled ? '/current_user' : null, () => {
    const fetchUserFn =
      redirectLoggedOutUsers || !enabled
        ? fetchUser
        : () => getCurrentTokenWithoutRedirect().then(() => fetchUser());
    return fetchUserFn().catch((e: unknown) => {
      redirectLoggedOutUsers &&
        console.error(
          'AlloyProvider - UserContext: Error fetching current user',
          e,
        );
      throw e;
    });
  });
  const data = error?.status === 401 ? null : fetchData;
  const loading = !data && !error;

  const setUser = useCallback((newData) => mutate(newData, false), [mutate]);

  return (
    <UserContext.Provider
      value={{
        error,
        loading,
        data,
        retry,
        setUser,
        setEnabled: enabled ? undefined : setEnabled,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.displayName = 'UserProvider';

UserProvider.defaultProps = {
  redirectLoggedOutUsers: true,
};

UserProvider.propTypes = {
  /** Whether to redirect logged out users to login page. */
  redirectLoggedOutUsers: PropTypes.bool,
  /** Whether we should disable this provider */
  disabled: PropTypes.bool,
  /** Nodes with access to context */
  children: PropTypes.node,
};
